import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  adChainsApi,
  appsApi,
  queryKeys,
  sourcePerformanceApi,
} from "../../lib/api/resources";
import type {
  AdChain,
  AdChainEntry,
  Placement,
  RegisteredApp,
  SourcePerformance,
} from "../../lib/api/types";
import { useToast } from "../../app/toast";
import { SourceDeviceBlocksCard } from "./SourceDeviceBlocksCard";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  NumberInput,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
} from "../../components/ui";

const PLACEMENTS: Placement[] = [
  "BANNER",
  "INTERSTITIAL",
  "REWARDED",
  "APP_OPEN",
];

/**
 * Sources the panel offers, purely as autocomplete.
 *
 * <p>The server accepts any name on purpose — that is what lets a network be configured before
 * every installed app implements it — so this list is a convenience, never a restriction. An
 * operator can type a source that is not here.
 */
const KNOWN_SOURCES = [
  "dramyeri",
  "admob",
  "applovin",
  "unity",
  "pangle",
  "mintegral",
];

/**
 * Mediation chains: which ad source serves each placement, and in what order.
 *
 * <p>This screen is the reason the mediation gateway exists. Before it, changing a network or an ad
 * unit meant a new app version and waiting for users to update; here it is a row an operator edits,
 * and installed apps pick it up when their cache expires.
 *
 * <p>Two things are worth understanding while editing:
 *
 * <ul>
 *   <li><strong>Order is revenue.</strong> The app tries sources top to bottom and stops at the
 *       first fill. Our own inventory (<code>dramyeri</code>) belongs first, because it has no
 *       revenue share.
 *   <li><strong>Changes are not instant.</strong> Apps cache the chain for its TTL, so an edit
 *       reaches users gradually rather than immediately.
 * </ul>
 */
export function AdChainListPage() {
  const [appFilter, setAppFilter] = useState("");
  const toast = useToast();
  const queryClient = useQueryClient();

  const chains = useQuery({
    queryKey: queryKeys.adChains(appFilter || undefined),
    queryFn: () => adChainsApi.list(appFilter || undefined),
  });

  // The registry, not the apps that happen to have chains already: a brand new
  // app must be choosable before it has any (ADS-0043).
  const activeApps = useQuery({
    queryKey: queryKeys.activeApps,
    queryFn: () => appsApi.active(),
  });

  /** Every mutation returns the whole chain, so the list is simply refetched. */
  const invalidate = () =>
    void queryClient
      .invalidateQueries({ queryKey: ["adChains"] })
      .then(() =>
        queryClient.invalidateQueries({ queryKey: queryKeys.adChainApps }),
      );

  const createChain = useMutation({
    mutationFn: adChainsApi.create,
    onSuccess: () => {
      toast.success("Cadena creada");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-headline-lg text-text-primary">Mediación</h1>
          <p className="text-body-md text-text-secondary">
            Qué red sirve cada espacio, y en qué orden. Los cambios llegan a las
            apps cuando expira su caché; no son inmediatos.
          </p>
        </div>
        <Select
          aria-label="Filtrar por app"
          value={appFilter}
          onChange={(event) => setAppFilter(event.target.value)}
          className="w-56"
        >
          <option value="">Todas las apps</option>
          {(activeApps.data ?? []).map((app) => (
            <option key={app.id} value={app.appId}>
              {app.displayName}
            </option>
          ))}
        </Select>
      </header>

      <SourcePerformanceCard appId={appFilter} />

      <SourceDeviceBlocksCard />

      <NewChainCard
        knownApps={activeApps.data ?? []}
        onCreate={(body) => createChain.mutate(body)}
        pending={createChain.isPending}
      />

      {chains.isLoading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : chains.isError ? (
        <Card>
          <ErrorState
            message={(chains.error as Error).message}
            onRetry={() => void chains.refetch()}
          />
        </Card>
      ) : chains.data!.length === 0 ? (
        <Card>
          <EmptyState
            title="Todavía no hay cadenas"
            description="Sin una cadena, la app usa la que trae compilada. Crea una para decidir desde aquí qué red sirve cada espacio."
          />
        </Card>
      ) : (
        chains.data!.map((chain) => (
          <ChainCard key={chain.id} chain={chain} onChanged={invalidate} />
        ))
      )}
    </div>
  );
}

/// Creates a chain.
///
/// The app is a `datalist` prefilled with the apps that already have chains, and
/// the button explains itself when it cannot be pressed.
///
/// Both exist because of a real confusion (ADS-0041): the field showed
/// `lottery-app` as placeholder text, which reads as a filled value, and the
/// button silently did nothing while it was empty. That looks exactly like the
/// server refusing the placement — someone lost real time concluding APP_OPEN
/// was unsupported when the request had never been sent.
function NewChainCard({
  knownApps,
  onCreate,
  pending,
}: {
  knownApps: RegisteredApp[];
  onCreate: (body: {
    appId: string;
    placement: Placement;
    format: Placement;
  }) => void;
  pending: boolean;
}) {
  const [appId, setAppId] = useState("");
  const [placement, setPlacement] = useState<Placement>("BANNER");

  const trimmedApp = appId.trim();
  const missingApp = trimmedApp.length === 0;

  return (
    <Card>
      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="App"
          hint="Solo las apps registradas pueden pedir anuncios"
        >
          <Select
            value={appId}
            className="w-64"
            onChange={(event) => setAppId(event.target.value)}
          >
            <option value="">Elige una app…</option>
            {/* Registered apps only (ADS-0043). A chain for an app that does
                not exist is configuration nothing will ever read. */}
            {knownApps.map((app) => (
              <option key={app.id} value={app.appId}>
                {app.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Espacio">
          <Select
            value={placement}
            className="w-48"
            onChange={(event) => setPlacement(event.target.value as Placement)}
          >
            {PLACEMENTS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex flex-col gap-1">
          <Button
            variant="primary"
            disabled={missingApp || pending}
            // Says why it cannot be pressed. A disabled button with no
            // explanation leaves an operator guessing whether the placement,
            // the server, or their own click was at fault.
            title={
              missingApp ? "Escribe el nombre de la app primero" : undefined
            }
            onClick={() =>
              onCreate({ appId: trimmedApp, placement, format: placement })
            }
          >
            Crear cadena
          </Button>
          {missingApp && (
            <span className="text-label-md text-text-secondary">
              Indica la app para continuar
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

function ChainCard({
  chain,
  onChanged,
}: {
  chain: AdChain;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  /**
   * One mutation for every action on this chain.
   *
   * <p>A single hook rather than one per action: hooks cannot be created in a helper or a loop, and
   * every one of these calls returns the same thing — the updated chain — so they differ only in
   * which request they send and what to say afterwards.
   */
  const act = useMutation({
    mutationFn: (action: { run: () => Promise<unknown>; message: string }) =>
      action.run(),
    onSuccess: (_data, action) => {
      toast.success(action.message);
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleChain = () =>
    act.mutate({
      run: () =>
        adChainsApi.update(chain.id, {
          format: chain.format,
          enabled: !chain.enabled,
          ttlSeconds: chain.ttlSeconds,
        }),
      message: chain.enabled ? "Cadena desactivada" : "Cadena activada",
    });

  const removeChain = () =>
    act.mutate({
      run: () => adChainsApi.remove(chain.id),
      message: "Cadena eliminada",
    });

  const removeEntry = (entryId: string) =>
    act.mutate({
      run: () => adChainsApi.removeEntry(chain.id, entryId),
      message: "Fuente eliminada",
    });

  const toggleEntry = (entry: AdChainEntry) =>
    act.mutate({
      run: () =>
        adChainsApi.updateEntry(chain.id, entry.id, {
          source: entry.source,
          timeoutMs: entry.timeoutMs,
          enabled: !entry.enabled,
          config: entry.config,
        }),
      message: "Fuente actualizada",
    });

  const addEntry = (body: {
    source: string;
    timeoutMs: number;
    config: Record<string, unknown>;
  }) =>
    act.mutate({
      run: () => adChainsApi.addEntry(chain.id, body),
      message: "Fuente agregada",
    });

  /** Moves one entry and sends the complete ordering, which is what the server requires. */
  const move = (index: number, delta: number) => {
    const ids = chain.entries.map((entry) => entry.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) {
      return;
    }
    [ids[index], ids[target]] = [ids[target], ids[index]];
    act.mutate({
      run: () => adChainsApi.reorder(chain.id, ids),
      message: "Orden actualizado",
    });
  };

  return (
    <Card className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-title-md text-text-primary">
            {chain.appId} · {chain.placement}
          </h2>
          <p className="font-mono text-label-md text-text-secondary">
            v{chain.version} · caché {chain.ttlSeconds}s
            {!chain.enabled && " · desactivada"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={toggleChain}>
            {chain.enabled ? "Desactivar" : "Activar"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              // Deleting a chain silently returns the app to its compiled-in fallback, which is
              // not obvious from the button alone.
              if (
                window.confirm(
                  `¿Eliminar la cadena de ${chain.appId} · ${chain.placement}? La app volverá a usar la cadena que trae compilada.`,
                )
              ) {
                removeChain();
              }
            }}
          >
            Eliminar
          </Button>
        </div>
      </header>

      {chain.entries.length === 0 ? (
        <EmptyState
          title="Sin fuentes"
          description="Una cadena vacía no sirve anuncios. Agrega al menos una fuente."
        />
      ) : (
        <ol className="flex flex-col gap-2">
          {chain.entries.map((entry, index) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-border-subtle px-3 py-2"
            >
              <span className="font-mono text-label-md text-text-secondary">
                {index + 1}
              </span>
              <span className="text-body-md text-text-primary">
                {entry.source}
              </span>
              <span className="font-mono text-label-md text-text-secondary">
                {entry.timeoutMs} ms
              </span>
              {Object.keys(entry.config ?? {}).length > 0 && (
                <span className="truncate font-mono text-label-md text-text-secondary">
                  {JSON.stringify(entry.config)}
                </span>
              )}
              {!entry.enabled && (
                <span className="text-label-md text-text-secondary">
                  desactivada
                </span>
              )}
              <div className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  aria-label="Subir"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  aria-label="Bajar"
                  disabled={index === chain.entries.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </Button>
                <Button variant="ghost" onClick={() => toggleEntry(entry)}>
                  {entry.enabled ? "Desactivar" : "Activar"}
                </Button>
                <Button variant="ghost" onClick={() => removeEntry(entry.id)}>
                  Quitar
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {adding ? (
        <NewEntryForm
          onCancel={() => setAdding(false)}
          onSubmit={(body) => {
            addEntry(body);
            setAdding(false);
          }}
        />
      ) : (
        <div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Agregar fuente
          </Button>
        </div>
      )}
    </Card>
  );
}

function NewEntryForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (body: {
    source: string;
    timeoutMs: number;
    config: Record<string, unknown>;
  }) => void;
  onCancel: () => void;
}) {
  const [source, setSource] = useState("");
  const [timeoutMs, setTimeoutMs] = useState(4000);
  const [config, setConfig] = useState("{}");
  const [configError, setConfigError] = useState<string | null>(null);

  const submit = () => {
    // Validated here as well as on the server: an operator pasting an ad unit id should be told
    // immediately that it is not an object, rather than after a round trip.
    let parsed: Record<string, unknown>;
    try {
      const value: unknown = JSON.parse(config || "{}");
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        setConfigError(
          'Debe ser un objeto JSON, por ejemplo {"adUnitId": "..."}',
        );
        return;
      }
      parsed = value as Record<string, unknown>;
    } catch {
      setConfigError("JSON inválido");
      return;
    }
    setConfigError(null);
    onSubmit({ source: source.trim(), timeoutMs, config: parsed });
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-subtle p-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Fuente">
          <Input
            value={source}
            list="known-ad-sources"
            placeholder="admob"
            className="w-56"
            onChange={(event) => setSource(event.target.value)}
          />
          <datalist id="known-ad-sources">
            {KNOWN_SOURCES.map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
        </Field>
        <Field label="Tiempo límite (ms)">
          <NumberInput
            value={timeoutMs}
            min={100}
            max={15000}
            className="w-44"
            onChange={(event) => setTimeoutMs(Number(event.target.value))}
          />
        </Field>
      </div>

      <Field
        label="Configuración de la red"
        hint='Se envía tal cual a la app. Ejemplo: {"androidAdUnitId": "ca-app-pub-.../..."}'
        error={configError ?? undefined}
      >
        <Input
          value={config}
          onChange={(event) => setConfig(event.target.value)}
        />
      </Field>

      <div className="flex items-center gap-2">
        <Button variant="primary" disabled={!source.trim()} onClick={submit}>
          Agregar
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

/**
 * What each source actually did, so chain order is a decision rather than a guess (ADS-0036).
 *
 * <p>Sits above the chains deliberately: the numbers are only useful next to the thing they should
 * change. An operator reads the fill rates and then reorders the chain below them.
 *
 * <p>These are client-reported diagnostics. `fills` is not an impression count and nothing here is
 * billable — the copy says so, because a column of numbers that looks like delivery invites being
 * read as delivery.
 */
function SourcePerformanceCard({ appId }: { appId: string }) {
  const performance = useQuery({
    queryKey: queryKeys.sourcePerformance(appId, 7),
    queryFn: () => sourcePerformanceApi.get(appId, 7),
    // Needs a specific app: totals across apps would average away the differences the operator is
    // looking for.
    enabled: Boolean(appId),
  });

  if (!appId) {
    return (
      <Card>
        <EmptyState
          title="Rendimiento por red"
          description="Elige una app arriba para ver qué red está rellenando cada espacio."
        />
      </Card>
    );
  }

  if (performance.isLoading) {
    return (
      <Card>
        <Skeleton className="h-32 w-full" />
      </Card>
    );
  }

  if (performance.isError) {
    return (
      <Card>
        <ErrorState
          message={(performance.error as Error).message}
          onRetry={() => void performance.refetch()}
        />
      </Card>
    );
  }

  const sources = performance.data?.sources ?? [];

  if (sources.length === 0) {
    return (
      <Card>
        <EmptyState
          title="Todavía no hay datos"
          description="Las apps reportan su rendimiento periódicamente. Aparecerá aquí en cuanto llegue el primer envío."
        />
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-3">
      <header>
        <h2 className="text-title-md text-text-primary">
          Rendimiento por red (7 días)
        </h2>
        <p className="text-body-md text-text-secondary">
          Lo que reportan las apps sobre cada fuente. Son datos de diagnóstico,
          no de facturación: los rellenos no son impresiones cobradas.
        </p>
      </header>

      <div className="overflow-x-auto">
        <Table>
          <thead>
            <tr>
              <Th>Espacio</Th>
              <Th>Fuente</Th>
              <Th>Intentos</Th>
              <Th>Rellenos</Th>
              <Th>% relleno</Th>
              <Th>Latencia</Th>
              <Th>Timeouts</Th>
              <Th>Errores</Th>
            </tr>
          </thead>
          <tbody>
            {sources.map((row: SourcePerformance) => (
              <tr key={`${row.placement}-${row.source}`}>
                <Td>{row.placement}</Td>
                <Td>{row.source}</Td>
                <Td>{row.attempts.toLocaleString("es-DO")}</Td>
                <Td>{row.fills.toLocaleString("es-DO")}</Td>
                <Td>{row.fillRate}%</Td>
                <Td>{row.averageLatencyMs} ms</Td>
                <Td>{row.timeouts.toLocaleString("es-DO")}</Td>
                <Td>{row.errors.toLocaleString("es-DO")}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );
}
